import React from 'react';
import { Link } from 'react-router-dom';

const ServiceCard = ({ title, description, delay = 0, index = 0 }) => {
  // Unique gradient and color for each service
  const gradients = [
    { 
      bg: 'from-purple-600 to-violet-700',
      border: 'border-purple-400',
      glow: 'group-hover:shadow-purple-500/50',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    { 
      bg: 'from-lavender-500 to-purple-600',
      border: 'border-lavender-400',
      glow: 'group-hover:shadow-lavender-500/50',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      )
    },
    { 
      bg: 'from-violet-600 to-purple-700',
      border: 'border-violet-400',
      glow: 'group-hover:shadow-violet-500/50',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {/* Atom icon */}
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
          <ellipse cx="12" cy="12" rx="9" ry="3.5" strokeWidth={1.5} />
          <ellipse cx="12" cy="12" rx="9" ry="3.5" strokeWidth={1.5} transform="rotate(60 12 12)" />
          <ellipse cx="12" cy="12" rx="9" ry="3.5" strokeWidth={1.5} transform="rotate(120 12 12)" />
        </svg>
      )
    },
    { 
      bg: 'from-purple-500 to-lavender-600',
      border: 'border-purple-300',
      glow: 'group-hover:shadow-purple-400/50',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    { 
      bg: 'from-lavender-600 to-violet-600',
      border: 'border-lavender-300',
      glow: 'group-hover:shadow-lavender-400/50',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {/* Pendulum icon */}
          <line x1="12" y1="2" x2="12" y2="15" strokeWidth={1.5} strokeLinecap="round" />
          <circle cx="12" cy="2" r="1" fill="currentColor" />
          <path d="M12 15 L9 20 L15 20 Z" fill="currentColor" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" />
          <path d="M6 18 Q12 16 18 18" strokeWidth={1} strokeDasharray="2,2" opacity="0.5" />
        </svg>
      )
    },
  ];

  const cardStyle = gradients[index % gradients.length];

  return (
    <Link
      to="/services"
      className={`group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl ${cardStyle.glow} transition-all duration-700 transform hover:-translate-y-4 hover:scale-105 animate-fade-in-up border-2 ${cardStyle.border} overflow-hidden block cursor-pointer`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Gradient background on hover */}
      <div className={`absolute inset-0 bg-gradient-to-br ${cardStyle.bg} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
      
      {/* Animated corner accent */}
      <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${cardStyle.bg} opacity-10 rounded-bl-full transform translate-x-16 -translate-y-16 group-hover:translate-x-8 group-hover:-translate-y-8 transition-transform duration-500`}></div>
      
      {/* Icon container */}
      <div className={`relative mb-6 w-16 h-16 bg-gradient-to-br ${cardStyle.bg} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500 rotate-0 group-hover:rotate-6`}>
        {cardStyle.icon}
        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 rounded-2xl transition-opacity duration-500"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10">
        <h3 className="text-xl md:text-2xl font-serif font-semibold text-purple-900 mb-4 group-hover:text-purple-700 transition-colors duration-300">
          {title}
        </h3>
        
        <p className="text-gray-600 leading-relaxed text-sm md:text-base group-hover:text-gray-700 transition-colors duration-300">
          {description}
        </p>
      </div>
      
      {/* Bottom gradient bar */}
      <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${cardStyle.bg} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
      
      {/* Sparkle effect on hover */}
      <div className="absolute top-4 right-4 w-2 h-2 bg-white rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-300"></div>
    </Link>
  );
};

export default ServiceCard;
